import React from 'react';
import Grid from '@mui/material/Grid';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Tooltip from '@mui/material/Tooltip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { AppContext, ThemeContext } from 'src/store/ContextStore';
import {
  CARRIER_MARK_STYLE,
  resolveCarrierStyle
} from '../../pedigree-tool/carrier-status/carrierStyle';
import { isAnonymousOrLoading, PII_LABEL_KEYS } from '../../anonymousPedigrees';
import {
  GROUPS_DERIVED,
  LABEL_BY_KEY,
  isFormattable
} from '../../pedigree-tool/pedigree/labelDefinitions';

// Brand teal for the active B/I formatting toggles (no new tokens — same teal
// the CONFIGURATIONS section uses).
const TEAL = '#2BA9BF';
const TEAL_HOVER = '#2596a8';

// Titles for the chart-behaviour switches that are NOT node labels (so they are
// deliberately absent from labelDefinitions). Node-label titles come from
// LABEL_BY_KEY — the single source of truth.
const labels = {
  showCarrierMarks: 'Show carrier marks',
  showAnalysisOverlay: 'Show pedigree analysis overlay',
  standardNomenclature: 'Standard pedigree nomenclature',
  showRecordStatus: 'Show record status'
};

// Node-label groups derive from the capability module so the list lives in one
// place. Per-test labels compose into a single line per test on the canvas —
// "<Test Name> (<Genes Tested>, <Test Result>, <Variants>)" — each segment
// shown only when its child toggle is on (see buildTestItems); Test Comments
// renders as its own line.
const GROUPS = GROUPS_DERIVED;

// A compact B / I toggle button matching the panel's control sizing + teal.
function FormatToggle({ kind, active, disabled, onToggle, t }) {
  const glyph = kind === 'bold' ? 'B' : 'I';
  const title =
    kind === 'bold'
      ? t(active ? 'Remove bold' : 'Bold')
      : t(active ? 'Remove italic' : 'Italic');
  return (
    <Tooltip title={disabled ? '' : title} arrow placement="top">
      {/* span wrapper so the tooltip works while disabled */}
      <span style={{ display: 'inline-flex' }}>
        <IconButton
          size="small"
          disableRipple={disabled}
          aria-pressed={active}
          aria-label={title}
          disabled={disabled}
          onClick={() => onToggle(!active)}
          sx={{
            width: 26,
            height: 26,
            borderRadius: 1,
            border: '1px solid',
            borderColor: active ? TEAL : 'rgba(0,0,0,0.23)',
            backgroundColor: active ? TEAL : 'transparent',
            color: active ? '#fff' : 'text.secondary',
            fontSize: 13,
            fontWeight: kind === 'bold' ? 700 : 500,
            fontStyle: kind === 'italic' ? 'italic' : 'normal',
            lineHeight: 1,
            '&:hover': {
              backgroundColor: active ? TEAL_HOVER : 'rgba(43,169,191,0.08)'
            },
            '&.Mui-disabled': { opacity: 0.4 }
          }}
        >
          {glyph}
        </IconButton>
      </span>
    </Tooltip>
  );
}

// Carrier marks control the bold disease-colour perimeter on carrier segments.
// Family-pedigree sidebar only (see LabelsMenu `isFamilyView`).
const CARRIERS_GROUP = {
  name: 'Carriers',
  keys: ['showCarrierMarks']
};

// Pedigree-analysis canvas overlay (inheritance pathway + cascade badges).
// Family-pedigree sidebar only; effect is global via labelsSet.showAnalysisOverlay.
const ANALYSIS_GROUP = {
  name: 'Pedigree analysis',
  keys: ['showAnalysisOverlay']
};

// Standard pedigree nomenclature: Roman generation labels (I = topmost) in a
// left gutter + consecutive left-to-right person numbers per generation, all
// DERIVED from the current layout (a snapshot, not persisted, not the UPN).
// Family-pedigree sidebar only; effect is global via labelsSet.standardNomenclature.
// Default OFF (defaultLabelFilters.standardNomenclature === false), independent
// of the UPN/name toggles.
const NOMENCLATURE_GROUP = {
  name: 'Nomenclature',
  keys: ['standardNomenclature']
};

// Record-status linkage badge: a teal top-left corner badge marking cross-family
// primary/secondary linked records (derived from PersonLink at render time).
// Family-pedigree sidebar only; effect is global via labelsSet.showRecordStatus.
// Default OFF (defaultLabelFilters.showRecordStatus === false), read via Boolean().
const RECORD_STATUS_GROUP = {
  name: 'Record status',
  keys: ['showRecordStatus']
};

function LabelGroup({
  group,
  labelsSet,
  onChange,
  onGroupChange,
  t,
  headerColor,
  footer,
  getFormat,
  onFormatChange
}) {
  const storageKey = `label-group-open:${group.name}`;
  const [open, setOpen] = React.useState(
    () => localStorage.getItem(storageKey) === 'true'
  );

  const toggle = () =>
    setOpen((prev) => {
      localStorage.setItem(storageKey, String(!prev));
      return !prev;
    });

  const checkedCount = group.keys.filter((k) => Boolean(labelsSet?.[k])).length;
  const allChecked = checkedCount === group.keys.length;
  const someChecked = checkedCount > 0 && !allChecked;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Checkbox
          size="small"
          checked={allChecked}
          indeterminate={someChecked}
          onChange={(e) => onGroupChange(group.keys, e.target.checked)}
        />
        <Typography
          variant="body1"
          onClick={toggle}
          sx={{
            fontWeight: 'bold',
            flexGrow: 1,
            cursor: 'pointer',
            userSelect: 'none',
            color: headerColor
          }}
        >
          {t(group.name)}
        </Typography>
        <IconButton size="small" onClick={toggle}>
          {open ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </IconButton>
      </div>
      <Collapse in={open}>
        <FormGroup sx={{ pl: 2 }}>
          {group.keys.map((labelKey) => {
            const def = LABEL_BY_KEY[labelKey];
            const title = t(def?.title || labels[labelKey] || labelKey);
            const visible = Boolean(labelsSet?.[labelKey]);
            const showFmt = isFormattable(labelKey);
            const fmt = showFmt ? getFormat(labelKey) : null;
            const formattable = def?.formattable || [];
            return (
              <Box
                key={labelKey}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  pr: 1
                }}
              >
                <FormControlLabel
                  sx={{ mr: 0 }}
                  control={
                    <Checkbox
                      checked={visible}
                      onChange={onChange}
                      size="small"
                      name={labelKey}
                    />
                  }
                  label={title}
                />
                {showFmt && (
                  <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                    {formattable.includes('bold') && (
                      <FormatToggle
                        kind="bold"
                        t={t}
                        active={Boolean(fmt?.bold)}
                        disabled={!visible}
                        onToggle={(val) =>
                          onFormatChange(labelKey, 'bold', val)
                        }
                      />
                    )}
                    {formattable.includes('italic') && (
                      <FormatToggle
                        kind="italic"
                        t={t}
                        active={Boolean(fmt?.italic)}
                        disabled={!visible}
                        onToggle={(val) =>
                          onFormatChange(labelKey, 'italic', val)
                        }
                      />
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
        </FormGroup>
        {footer}
      </Collapse>
    </div>
  );
}

export default function LabelsMenu(props) {
  const { labelsSet, setLabelsSet, isFamilyView, diagnoses = [] } = props;
  const t = useAutoTranslation();
  const { theme } = React.useContext(ThemeContext);
  const { content } = React.useContext(AppContext);
  const anonymous = isAnonymousOrLoading(content?.settings?.pedigreeSetting);

  // The Carriers group is scoped to the family pedigree sidebar (no node
  // selected); its effect is global via labelsSet.showCarrierMarks.
  const baseGroups = isFamilyView
    ? [
        ...GROUPS,
        CARRIERS_GROUP,
        ANALYSIS_GROUP,
        NOMENCLATURE_GROUP,
        RECORD_STATUS_GROUP
      ]
    : GROUPS;

  // Anonymous pedigrees: hide the PII label toggles (the canvas clamps them at
  // render time anyway). upn/age/personNote stay — UPN is the non-identifying
  // node handle.
  const groups = anonymous
    ? baseGroups.map((group) =>
        group.name === 'Personal'
          ? {
              ...group,
              keys: group.keys.filter((k) => !PII_LABEL_KEYS.includes(k))
            }
          : group
      )
    : baseGroups;

  const handleChange = (e) => {
    const updated = { ...labelsSet, [e.target.name]: e.target.checked };
    if (e.target.name === 'confirmedDiagnosis' && e.target.checked) {
      updated.diseaseWithAge = true;
    }
    if (e.target.name === 'diseaseWithAge' && !e.target.checked) {
      updated.confirmedDiagnosis = false;
    }
    if (e.target.name === 'diseaseWithAge' && e.target.checked) {
      updated.confirmedDiagnosis = true;
    }
    setLabelsSet(updated);
  };

  const handleGroupChange = (keys, checked) => {
    const updated = { ...labelsSet };
    keys.forEach((k) => {
      updated[k] = checked;
    });
    if (checked && keys.includes('diseaseWithAge')) {
      updated.confirmedDiagnosis = true;
    }
    if (!checked && keys.includes('diseaseWithAge')) {
      updated.confirmedDiagnosis = false;
    }
    if (checked && keys.includes('confirmedDiagnosis')) {
      updated.diseaseWithAge = true;
    }
    setLabelsSet(updated);
  };

  // Per-label bold/italic formatting — always available, like the visibility
  // checkboxes themselves.
  const getFormat = (key) => labelsSet?.formatting?.[key] || {};

  const handleFormatChange = (key, attr, value) => {
    setLabelsSet((prev) => {
      const formatting = { ...(prev?.formatting || {}) };
      const current = { ...(formatting[key] || {}), [attr]: value };
      // Keep the map sparse: an entry with no active style is dropped, so an
      // all-off formatting map is `{}` and adds nothing to the render.
      if (current.bold || current.italic) formatting[key] = current;
      else delete formatting[key];
      return { ...prev, formatting };
    });
  };

  // Carrier mark STYLE selector (border vs Bennett-2022 vertical line). This
  // governs only the gene→disease carrier marks; the legacy +/- genotype
  // Carrier-Status annotation is a separate pipeline and is intentionally NOT
  // touched here. Vertical line is only valid when a single disease is filtered,
  // so it disables (and the renderer auto-falls-back to border) otherwise.
  const visibleDiseaseCount =
    labelsSet?.diseases === false
      ? 0
      : (diagnoses || []).filter(
          (d) => labelsSet?.diseasesMap?.[d.id] !== false
        ).length;
  const preferredStyle =
    labelsSet?.carrierMarkStyle || CARRIER_MARK_STYLE.border;
  const { fallbackActive } = resolveCarrierStyle(
    preferredStyle,
    visibleDiseaseCount
  );
  const verticalLineEnabled = visibleDiseaseCount === 1;

  // Sub-section shown under "Show carrier marks" (inside the Carriers group) via
  // LabelGroup's footer slot. Options are indented under the label and laid out
  // horizontally with a slightly smaller font.
  const labelSx = { '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } };
  const carrierStyleSelector = (
    <Box sx={{ pl: 2, pr: 1, pb: 0.5 }}>
      <Typography
        variant="caption"
        sx={{ fontWeight: 600, color: 'text.secondary' }}
      >
        {t('Style')}
      </Typography>
      <RadioGroup
        row
        value={preferredStyle}
        onChange={(e) =>
          setLabelsSet((prev) => ({
            ...prev,
            carrierMarkStyle: e.target.value
          }))
        }
        sx={{ pl: 1 }}
      >
        <FormControlLabel
          value={CARRIER_MARK_STYLE.border}
          control={<Radio size="small" />}
          label={t('Border')}
          sx={labelSx}
        />
        <Tooltip
          title={
            verticalLineEnabled
              ? ''
              : t(
                  'Vertical line style is available when only one disease is shown on the pedigree.'
                )
          }
          placement="top"
          arrow
        >
          {/* span wrapper so the tooltip works while the radio is disabled */}
          <span style={{ display: 'inline-flex', width: 'fit-content' }}>
            <FormControlLabel
              value={CARRIER_MARK_STYLE.verticalLine}
              disabled={!verticalLineEnabled}
              control={<Radio size="small" />}
              label={t('Vertical line')}
              sx={labelSx}
            />
          </span>
        </Tooltip>
      </RadioGroup>
      {fallbackActive && (
        <Typography
          variant="caption"
          sx={{ display: 'block', color: 'warning.main', mt: 0.5, pl: 1 }}
        >
          {t(
            'Showing as Border because more than one disease is visible. Vertical line will return when filtered to a single disease.'
          )}
        </Typography>
      )}
    </Box>
  );

  return (
    <Grid
      container
      sx={{
        alignItems: 'center'
      }}
    >
      <Grid size={12}>
        {groups.map((group) => (
          <LabelGroup
            key={group.name}
            group={group}
            labelsSet={labelsSet}
            onChange={handleChange}
            onGroupChange={handleGroupChange}
            t={t}
            headerColor={theme.header}
            getFormat={getFormat}
            onFormatChange={handleFormatChange}
            footer={
              isFamilyView &&
              group.keys.includes('showCarrierMarks') &&
              labelsSet?.showCarrierMarks
                ? carrierStyleSelector
                : null
            }
          />
        ))}
      </Grid>
    </Grid>
  );
}
